import {
  DEFAULT_IMPORT_MODULE_NAME,
  GOLF_MODULE_NAME,
  SPA_TRANSACTIONS_MODULE_NAME,
} from "./moduleNames";

export type OnboardingImportSheetItem = {
  excelSheetName: string;
  importData: string;
  totalRows: number;
};

/** Body shape for POST /OnBoarding/ImportExcelData (one sheet per request). */
export type OnboardingImportExcelDataBody = {
  tenantId: number;
  propertyId: number;
  productId: number;
  fileName: string;
  moduleName: string;
  dataUtilityImportData: OnboardingImportSheetItem[];
  base64ExportFileData: string;
};

const ALLOWED_MODULE_NAMES = new Set([
  DEFAULT_IMPORT_MODULE_NAME,
  SPA_TRANSACTIONS_MODULE_NAME,
  GOLF_MODULE_NAME,
]);

const isPositiveInt = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

const isNonNegativeNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

/**
 * Validates wire format for OnBoarding ImportExcelData.
 * Returns human-readable errors (empty = ok). Does not call the API.
 */
export const validateOnboardingImportExcelDataBody = (
  body: OnboardingImportExcelDataBody,
): string[] => {
  const errors: string[] = [];

  if (!isPositiveInt(body.tenantId)) {
    errors.push("tenantId is required and must be greater than zero");
  }
  if (!isPositiveInt(body.propertyId)) {
    errors.push("propertyId is required and must be greater than zero");
  }
  if (!isPositiveInt(body.productId)) {
    errors.push("productId is required and must be greater than zero");
  }

  if (typeof body.fileName !== "string" || !body.fileName.trim()) {
    errors.push("fileName is required");
  } else if (!body.fileName.toLowerCase().endsWith(".xlsx")) {
    errors.push(`fileName must end with .xlsx (got "${body.fileName}")`);
  }

  if (typeof body.moduleName !== "string" || !body.moduleName.trim()) {
    errors.push("moduleName is required");
  } else if (!ALLOWED_MODULE_NAMES.has(body.moduleName.trim())) {
    errors.push(
      `moduleName must be one of: ${Array.from(ALLOWED_MODULE_NAMES).join(", ")} (got "${body.moduleName}")`,
    );
  }

  if (
    typeof body.base64ExportFileData !== "string" ||
    !body.base64ExportFileData.trim()
  ) {
    errors.push("base64ExportFileData is required");
  }

  if (!Array.isArray(body.dataUtilityImportData)) {
    errors.push("dataUtilityImportData must be an array");
    return errors;
  }

  if (body.dataUtilityImportData.length !== 1) {
    errors.push(
      `dataUtilityImportData must contain exactly one sheet (got ${body.dataUtilityImportData.length})`,
    );
  }

  body.dataUtilityImportData.forEach((item, index) => {
    const label = `dataUtilityImportData[${index}]`;
    if (!item || typeof item !== "object") {
      errors.push(`${label} must be an object`);
      return;
    }

    if (
      typeof item.excelSheetName !== "string" ||
      !item.excelSheetName.trim()
    ) {
      errors.push(`${label}.excelSheetName is required`);
    }

    if (typeof item.importData !== "string") {
      errors.push(
        `${label}.importData must be a JSON string (got ${typeof item.importData})`,
      );
    } else if (!item.importData.trim()) {
      errors.push(`${label}.importData must not be empty`);
    } else {
      try {
        const parsed: unknown = JSON.parse(item.importData);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          errors.push(
            `${label}.importData must parse to a JSON object (sheet name → rows)`,
          );
        } else if (item.excelSheetName?.trim()) {
          const keys = Object.keys(parsed as Record<string, unknown>);
          if (keys.length > 0 && !keys.includes(item.excelSheetName.trim())) {
            errors.push(
              `${label}.importData should include key "${item.excelSheetName}" (found: ${keys.join(", ") || "none"})`,
            );
          }
        }
      } catch {
        errors.push(`${label}.importData is not valid JSON`);
      }
    }

    if (!isNonNegativeNumber(item.totalRows)) {
      errors.push(`${label}.totalRows must be a number >= 0`);
    } else if (
      body.moduleName === SPA_TRANSACTIONS_MODULE_NAME &&
      item.totalRows !== 0
    ) {
      errors.push(
        `${label}.totalRows must be 0 for moduleName "${SPA_TRANSACTIONS_MODULE_NAME}"`,
      );
    }
  });

  return errors;
};

export const assertOnboardingImportExcelDataBody = (
  body: OnboardingImportExcelDataBody,
): void => {
  const errors = validateOnboardingImportExcelDataBody(body);
  if (errors.length > 0) {
    throw new Error(`Invalid ImportExcelData request: ${errors.join("; ")}`);
  }
};
